import { Check, ChevronDown, FilterLines, Hash02, Package, SearchLg, User01, ZapFast } from "@untitledui/icons";
import { useEffect, useState } from "react";
import type { SampleRequestView } from "@/api/sample-requests";
import { Button } from "@/components/base/buttons/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useClearableDebounce } from "@/hooks/use-debounce";

const GROUP_BY_OPTIONS = [
  { id: "product", label: "Product", icon: Package, searchPlaceholder: "Search Product" },
  { id: "sku", label: "SKU", icon: Hash02, searchPlaceholder: "Search SKU" },
  { id: "creator", label: "Creator", icon: User01, searchPlaceholder: "Search Creator" },
] as const;

export function SampleRulesButton({ rulesCount, onRulesClick }: { rulesCount: number; onRulesClick: () => void }) {
  return (
    <Button color="secondary" size="sm" iconLeading={<ZapFast className="size-4" />} onClick={onRulesClick}>
      {rulesCount > 0 ? `See Rules (${rulesCount})` : "Add Rules"}
    </Button>
  );
}

export function SampleRequestsControls({
  view,
  onViewChange,
  onSearchChange,
  onFilterClick,
}: {
  view: SampleRequestView;
  onViewChange: (view: SampleRequestView) => void;
  onSearchChange: (search: string) => void;
  onFilterClick: () => void;
}) {
  const [searchInput, setSearchInput] = useState("");
  const { value: debouncedSearch } = useClearableDebounce(searchInput, 300);

  useEffect(() => {
    onSearchChange(debouncedSearch);
  }, [debouncedSearch, onSearchChange]);

  const activeOption = GROUP_BY_OPTIONS.find((opt) => opt.id === view) ?? GROUP_BY_OPTIONS[0];

  return (
    <div className="flex flex-row flex-wrap items-center justify-between gap-4">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Group by"
            className="flex items-center gap-2 rounded-lg border border-secondary bg-primary px-3.5 py-2 text-sm font-semibold text-primary transition-colors hover:bg-secondary"
          >
            <span className="font-normal text-tertiary">Group by</span>
            {activeOption.label}
            <ChevronDown className="size-4 text-tertiary" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[160px]">
          {GROUP_BY_OPTIONS.map((opt) => (
            <DropdownMenuItem key={opt.id} className="gap-2" onSelect={() => onViewChange(opt.id)}>
              <opt.icon className="size-4 text-tertiary" />
              {opt.label}
              {view === opt.id && <Check className="ml-auto size-4 text-brand-secondary" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="flex items-center gap-2">
        <div className="flex w-72 items-center gap-2 rounded-lg border border-secondary px-3 py-2">
          <SearchLg className="size-4 text-tertiary" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={activeOption.searchPlaceholder}
            className="w-full bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
          />
        </div>
        <Button color="secondary" size="sm" iconLeading={<FilterLines className="size-4" />} onClick={onFilterClick}>
          Filter
        </Button>
      </div>
    </div>
  );
}
